import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Save, Plus, Trash2, User, Printer } from 'lucide-react';
import { prescriptionService } from '../../consultation/api/prescriptions';
import { encounterService } from '../../consultation/api/encounters';
import { patientService } from '../../../lib/services';
import { prescriptionSchema, type PrescriptionFormData } from '../../consultation/schemas/prescriptionSchema';
import { useStore } from '../../../lib/store';
import { getDoctorIdFromUser, isDoctor } from '../../../lib/rbac';
import { printPrescription } from '../../consultation/utils/prescriptionPdf';
import toast from 'react-hot-toast';
import type { Patient, Doctor } from '../../../lib/supabase';
import type { Encounter } from '../../consultation/types';

const PrescriptionNew: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useStore();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const doctorId = getDoctorIdFromUser(user);
  const patientId = searchParams.get('patientId');
  const encounterId = searchParams.get('encounterId');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PrescriptionFormData>({
    resolver: zodResolver(prescriptionSchema),
    defaultValues: {
      patientId: patientId || '',
      items: [
        {
          nameOrMedicineId: '',
          dose: '',
          frequency: '',
          duration: '',
          route: 'oral',
          instructions: '',
        },
      ],
      advice: '',
      followUpDate: '',
      signedBy: user?.email || '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedPatientId = watch('patientId');

  useEffect(() => {
    if (!isDoctor(user)) {
      toast.error('Access denied. Doctor privileges required.');
      navigate('/');
      return;
    }
    
    if (!doctorId) {
      toast.error('Doctor ID not found. Please contact administrator.');
      navigate('/');
      return;
    }

    loadData();
  }, [user, doctorId, navigate]);

  useEffect(() => {
    if (watchedPatientId && typeof watchedPatientId === 'string') {
      loadPatientEncounters(watchedPatientId);
    }
  }, [watchedPatientId]);

  const loadData = async () => {
    if (!doctorId) return;
    
    try {
      setLoading(true);
      
      const [patientsData, encountersData] = await Promise.all([
        patientService.getAll(),
        encounterService.getByDoctor(doctorId)
      ]);
      
      setPatients(patientsData);
      setEncounters(encountersData);

      if (patientId) {
        const patient = patientsData.find((p: any) => p.id === patientId);
        if (patient) {
          setSelectedPatient(patient);
          setValue('patientId', patientId);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const loadPatientEncounters = async (patientId: string) => {
    if (!doctorId) return;
    
    try {
      const allEncounters = await encounterService.getByDoctor(doctorId);
      const patientEncounters = allEncounters.filter(e => e.patientId === patientId);
      setEncounters(patientEncounters);
      
      const patient = patients.find(p => p.id === patientId);
      setSelectedPatient(patient || null);
    } catch (error) {
      console.error('Failed to load patient encounters:', error);
    }
  };

  const onSubmit = async (data: PrescriptionFormData) => {
    if (!doctorId) {
      toast.error('Doctor ID not found');
      return;
    }

    setSaving(true);
    try {
      let targetEncounterId = encounterId;
      
      if (!targetEncounterId && encounters.length > 0) {
        const recentEncounter = encounters
          .filter(e => e.patientId === data.patientId)
          .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())[0];
        
        if (recentEncounter) {
          targetEncounterId = recentEncounter.id;
        }
      }

      if (!targetEncounterId) {
        const encounterData = {
          patientId: data.patientId,
          doctorId: doctorId,
          dateTime: new Date().toISOString(),
          status: 'final' as const,
          complaints: 'Prescription consultation',
          notes: 'Created for prescription',
        };
        
        const newEncounter = await encounterService.create(encounterData);
        targetEncounterId = newEncounter.id;
      }

      const prescription = await prescriptionService.create(targetEncounterId, data);
      toast.success('Prescription created successfully');
      navigate(`/doctor/prescriptions/${prescription.id}`);
    } catch (error: any) {
      console.error('Failed to create prescription:', error);
      toast.error(error.message || 'Failed to create prescription');
    } finally {
      setSaving(false);
    }
  };

  const onSaveAndPrint = async (data: PrescriptionFormData) => {
    if (!doctorId) {
      toast.error('Doctor ID not found');
      return;
    }

    setSaving(true);
    try {
      let targetEncounterId = encounterId;
      
      if (!targetEncounterId && encounters.length > 0) {
        const recentEncounter = encounters
          .filter(e => e.patientId === data.patientId)
          .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())[0];
        
        if (recentEncounter) {
          targetEncounterId = recentEncounter.id;
        }
      }

      if (!targetEncounterId) {
        const encounterData = {
          patientId: data.patientId,
          doctorId: doctorId,
          dateTime: new Date().toISOString(),
          status: 'final' as const,
          complaints: 'Prescription consultation',
          notes: 'Created for prescription',
        };
        
        const newEncounter = await encounterService.create(encounterData);
        targetEncounterId = newEncounter.id;
      }

      const prescription = await prescriptionService.create(targetEncounterId, data);
      
      const encounterWithPatient = {
        id: targetEncounterId,
        patientId: data.patientId,
        doctorId: doctorId,
        dateTime: new Date().toISOString(),
        status: 'final' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        patient: selectedPatient,
        doctor: { 
          id: doctorId, 
          name: user?.email?.split('@')[0] || 'Doctor',
          email: user?.email || '',
          specialization: 'General Practice',
          license_number: 'MD-' + doctorId.slice(-5)
        } as Doctor,
      };

      printPrescription(prescription, encounterWithPatient);
      toast.success('Prescription created and sent to printer');
      navigate(`/doctor/prescriptions/${prescription.id}`);
    } catch (error: any) {
      console.error('Failed to create and print prescription:', error);
      toast.error(error.message || 'Failed to create prescription');
    } finally {
      setSaving(false);
    }
  };

  if (!isDoctor(user)) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Doctor privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Prescription</h1>
          <p className="text-gray-600">Create a new prescription for your patient</p>
        </div>
      </div>

      {selectedPatient && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center space-x-4">
            <User className="h-12 w-12 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold">{selectedPatient.name}</h3>
              <p className="text-sm text-gray-600">{selectedPatient.email}</p>
              <p className="text-sm text-gray-600">{selectedPatient.phone}</p>
              {selectedPatient.date_of_birth && (
                <p className="text-sm text-gray-600">
                  DOB: {new Date(selectedPatient.date_of_birth).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <form className="space-y-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Patient Selection</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">Patient</label>
            <select
              {...register('patientId')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a patient</option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.name} - {patient.email}
                </option>
              ))}
            </select>
            {errors.patientId && (
              <p className="mt-1 text-sm text-red-600">{errors.patientId.message}</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Medications</h3>
            <button
              type="button"
              onClick={() => append({
                nameOrMedicineId: '',
                dose: '',
                frequency: '',
                duration: '',
                route: 'oral',
                instructions: '',
              })}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
            >
              <Plus className="w-4 h-4" />
              <span>Add Medication</span>
            </button>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Medication {index + 1}</h4>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Medicine Name</label>
                    <input
                      {...register(`items.${index}.nameOrMedicineId`)}
                      placeholder="e.g., Paracetamol"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Dose</label>
                    <input
                      {...register(`items.${index}.dose`)}
                      placeholder="e.g., 500mg"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Frequency</label>
                    <input
                      {...register(`items.${index}.frequency`)}
                      placeholder="e.g., Twice daily"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duration</label>
                    <input
                      {...register(`items.${index}.duration`)}
                      placeholder="e.g., 7 days"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Route</label>
                    <select
                      {...register(`items.${index}.route`)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="oral">Oral</option>
                      <option value="topical">Topical</option>
                      <option value="injection">Injection</option>
                      <option value="inhalation">Inhalation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700">Instructions</label>
                    <input
                      {...register(`items.${index}.instructions`)}
                      placeholder="e.g., Take after meals"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Clinical Advice</label>
              <textarea
                {...register('advice')}
                rows={3}
                placeholder="General advice for the patient..."
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Follow-up Date</label>
              <input
                type="date"
                {...register('followUpDate')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/doctor/prescriptions')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Prescription'}</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSaveAndPrint)}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save & Print'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default PrescriptionNew;
